import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

// Custom hooks
import useToolData from '../hooks/useToolData';

// Common components
import HtmlContent from '../components/common/HtmlContent';
import ToolInfoTable from '../components/tools/ToolInfoTable';
import ResourcesList from '../components/tools/ResourcesList';
import Notes from '../components/tools/Notes';

/**
 * ToolPage Component
 * 
 * Renders a tool page with the exact visual structure preserved from the legacy site.
 */
const ToolPage = () => {
  const { category, subcategory, toolId } = useParams();
  const navigate = useNavigate();
  const { data: toolData, loading, error } = useToolData('tool', category, subcategory, toolId);

  // Redirect to category page if tool not found
  useEffect(() => {
    if (error && !loading) {
      navigate(`/${category}#${subcategory}`);
    }
  }, [error, loading, navigate, category, subcategory]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error || !toolData) {
    return <div className="not-found">Tool not found</div>;
  }

  return (
    <main className="case-study">
      <span className="case-tool-title" id={toolData.slug}>&nbsp;&nbsp;</span>
      <div className="linebreak"></div>
      <div className="tool-title-wrap">
        <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
        <div className="tool-title" id={toolData.slug}>
          <span className="tool-title-number">
            <a href={`#${toolData.slug}`}>{toolData.title}</a>
          </span>
        </div>
        <div className="tool-title-nav">
          <Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;
          <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
          <Link to={`/${category}`}>{toolData.categoryTitle || category}</Link>&nbsp;&nbsp;
          <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
          <Link to={`/${category}/${subcategory}`}>{toolData.subcategoryTitle || subcategory}</Link>
        </div>
        <div className="tool-title-link">
          <a href={`#${toolData.slug}`}><i className="fas fa-link fa-2x"></i></a>
        </div>
      </div>
      <div className="linebreak"></div>

      {toolData.tabs && toolData.tabs.length > 0 && (
        <div id="tabs" className="tabs">
          {toolData.tabs.map((tab, index) => (
            <React.Fragment key={tab.id}>
              <input 
                type="radio" 
                name={`tabs-${toolData.slug}`} 
                id={`tab-${tab.id}`} 
                checked={index === 0 ? "checked" : undefined}
              />
              <label htmlFor={`tab-${tab.id}`}>{tab.label}</label>
              
              <div className="tab tab-tools">
                {tab.type === 'overview' && (
                  <>
                    <div className="tool-opening-container">
                      {toolData.toolInfo && (
                        <div className="tool-table-wrap">
                          <ToolInfoTable toolInfo={toolData.toolInfo} />
                        </div>
                      )}
                      
                      <div className="tool-opening">
                        <HtmlContent html={tab.html} containerClass="" />
                      </div>
                    </div>
                    
                    {/* Resources section - only if present */}
                    {toolData.resources && toolData.resources.length > 0 && (
                      <ResourcesList resources={toolData.resources} />
                    )}
                    
                    {/* Notes section - only if present */}
                    {toolData.notes && (
                      <Notes notes={toolData.notes} />
                    )}
                  </>
                )}
                
                {tab.type === 'template' && tab.url && (
                  <iframe 
                    className="miro_embed" 
                    src={tab.url} 
                    frameBorder="0" 
                    scrolling="no" 
                    allowFullScreen
                    title={`${toolData.title} template`}
                  />
                )}
                
                {tab.type === 'extra' && tab.html && (
                  <div className="tool-opening-container">
                    <div className="tool-opening">
                      <HtmlContent html={tab.html} containerClass="" />
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </main>
  );
};

export default ToolPage; 